import React, { useState, useEffect } from 'react';
import StableMuiDialog from '../../../../common/StableMuiDialog.js';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import LinkIcon from '@mui/icons-material/Link';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { SpreadsheetOfferOptions, getSpreadsheetConfirmPayload } from '../SendSpreadsheetOffersDialog.js';
import { TOWN_PORTAL_DELIVERY_METHODS } from './SendTownPortalOffers.js';
import LinkPermissionOptions from './LinkPermissionOptions.js';
import {
  buildLinkOptions,
  buildPermissionStateFromSaved,
} from './townPortalLinkPermissions.js';
import { fetchCounterpartyLinkDefaults } from './SendTownPortalOffers.js';

export const colors = {
  bgPrimary: '#ffffff',
  bgSecondary: '#f1efe8',
  textPrimary: '#1a1a18',
  textSecondary: '#5f5e5a',
  textTertiary: '#888780',
  borderTertiary: 'rgba(0,0,0,0.12)',
  borderSecondary: 'rgba(0,0,0,0.22)',
  accent: '#185FA5',
  accentBg: '#E6F1FB',
  accentBorder: '#B5D4F4',
  warn: '#BA7517',
};

export const DIALOG_PAPER_SX = {
  width: '100%',
  borderRadius: '16px',
  border: `0.5px solid ${colors.borderSecondary}`,
  bgcolor: colors.bgPrimary,
  m: 2,
};

export const CANCEL_BUTTON_SX = {
  fontSize: 13,
  fontWeight: 500,
  color: colors.textSecondary,
  border: `0.5px solid ${colors.borderSecondary}`,
  borderRadius: '8px',
  px: 2,
  py: 1,
  textTransform: 'none',
  minWidth: 'auto',
};

export const PRIMARY_BUTTON_SX = {
  fontSize: 13,
  fontWeight: 500,
  bgcolor: colors.accent,
  borderRadius: '8px',
  px: 2.5,
  py: 1,
  textTransform: 'none',
  minWidth: 'auto',
  '&:hover': { bgcolor: '#144f8a' },
};

export const SectionLabel = ({ children }) => (
  <Typography
    component="p"
    sx={{
      fontSize: 11,
      fontWeight: 500,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: colors.textTertiary,
      m: '0 0 8px',
    }}
  >
    {children}
  </Typography>
);

export const MethodCard = ({ active, legacy, disabled, badge, badgeLegacy, icon, name, description, onClick }) => (
  <Box
    onClick={disabled ? undefined : onClick}
    role={disabled ? undefined : 'button'}
    tabIndex={disabled ? undefined : 0}
    onKeyDown={
      disabled
        ? undefined
        : (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClick();
            }
          }
    }
    sx={{
      border: '1.5px solid',
      borderColor: active
        ? (legacy ? colors.borderSecondary : colors.accent)
        : colors.borderTertiary,
      borderRadius: '8px',
      p: '12px 14px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      position: 'relative',
      transition: 'border-color 0.15s, background 0.15s',
      background: active ? (legacy ? colors.bgSecondary : colors.accentBg) : colors.bgPrimary,
      opacity: disabled ? 0.55 : legacy && !active ? 0.7 : 1,
      '&:hover': disabled ? {} : { borderColor: active ? undefined : colors.borderSecondary },
    }}
  >
    {badge && <Box
      component="span"
      sx={{
        position: 'absolute',
        top: 8,
        right: 8,
        fontSize: 10,
        fontWeight: 500,
        px: '7px',
        py: '2px',
        borderRadius: '20px',
        border: '0.5px solid',
        ...(badgeLegacy
          ? {
              background: colors.bgSecondary,
              color: colors.textTertiary,
              borderColor: colors.borderTertiary,
            }
          : {
              background: colors.accentBg,
              color: colors.accent,
              borderColor: colors.accentBorder,
            }),
      }}
    >
      {badge}
    </Box>}
    <Box sx={{ fontSize: 20, color: active && !legacy ? colors.accent : colors.textSecondary, mb: 0.75 }}>
      {icon}
    </Box>
    <Typography sx={{ fontSize: 13, fontWeight: 500, color: colors.textPrimary, m: '0 0 2px' }}>
      {name}
    </Typography>
    <Typography sx={{ fontSize: 11, color: colors.textSecondary, m: 0, lineHeight: 1.4 }}>
      {description}
    </Typography>
  </Box>
);

export const OptionsPanel = ({ visible, children }) => (
  <Box
    sx={{
      border: `0.5px solid ${colors.borderTertiary}`,
      borderRadius: '8px',
      p: 1.75,
      mb: 2.5,
      display: visible ? 'block' : 'none',
    }}
  >
    {children}
  </Box>
);

/**
 * Town portal beta dialog: link delivery (default) or spreadsheet (standard offer options).
 */
const TownPortalOffersDialog = ({
  open,
  onClose,
  onConfirm,
  negotiationObj,
  defaultWorkupReportType = 'fnmaSummary',
}) => {
  const [sendMethod, setSendMethod] = useState(TOWN_PORTAL_DELIVERY_METHODS.LINK);
  const [permissionState, setPermissionState] = useState(() => buildPermissionStateFromSaved());

  useEffect(() => {
    if (!open) return;

    setSendMethod(TOWN_PORTAL_DELIVERY_METHODS.LINK);
    setPermissionState(buildPermissionStateFromSaved());

    let cancelled = false;
    void (async () => {
      const result = await fetchCounterpartyLinkDefaults({ negotiationObj });
      if (cancelled || !result.success || !result.found) return;
      setPermissionState(result.permissionState);
    })();

    return () => {
      cancelled = true;
    };
  }, [open, negotiationObj?.MuniCode, negotiationObj?.TaxYear, negotiationObj?.CourtDate]);

  const isLink = sendMethod === TOWN_PORTAL_DELIVERY_METHODS.LINK;
  const isSpreadsheet = sendMethod === TOWN_PORTAL_DELIVERY_METHODS.SPREADSHEET;

  const handleContinue = async () => {
    onClose();

    if (isLink) {
      await onConfirm({
        deliveryMethod: TOWN_PORTAL_DELIVERY_METHODS.LINK,
        linkOptions: buildLinkOptions(permissionState),
      });
      return;
    }

    const { workupOptions, workupReportType } = getSpreadsheetConfirmPayload(defaultWorkupReportType);
    await onConfirm({
      deliveryMethod: TOWN_PORTAL_DELIVERY_METHODS.SPREADSHEET,
      workupOptions,
      workupReportType,
    });
  };

  return (
    <StableMuiDialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{ sx: { ...DIALOG_PAPER_SX, maxWidth: 460 } }}
    >
      <Box sx={{ p: 3 }}>
        <Typography
          component="h2"
          sx={{ fontSize: 17, fontWeight: 500, color: colors.textPrimary, m: '0 0 4px' }}
        >
          Send offers
        </Typography>
        <Typography sx={{ fontSize: 13, color: colors.textSecondary, m: '0 0 20px' }}>
          Choose how to deliver offers to the counterparty
        </Typography>

        <SectionLabel>Delivery method</SectionLabel>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 1,
            mb: 2.5,
          }}
        >
          <MethodCard
            active={isLink}
            badge="Recommended"
            icon={<LinkIcon fontSize="inherit" />}
            name="Portal link"
            description="Town access portal with live data"
            onClick={() => setSendMethod(TOWN_PORTAL_DELIVERY_METHODS.LINK)}
          />
          <MethodCard
            active={isSpreadsheet}
            legacy
            badgeLegacy
            badge="Legacy"
            icon={<TableChartOutlinedIcon fontSize="inherit" />}
            name="Spreadsheet"
            description="Email with offers spreadsheet"
            onClick={() => setSendMethod(TOWN_PORTAL_DELIVERY_METHODS.SPREADSHEET)}
          />
        </Box>

        {isSpreadsheet && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              mb: 1.5,
              px: 1.25,
              py: 1,
              borderRadius: '8px',
              bgcolor: '#FEF6E8',
              border: '0.5px solid #F0D4A8',
            }}
          >
            <WarningAmberOutlinedIcon sx={{ fontSize: 15, color: colors.warn, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 12, color: '#7A5A1A', lineHeight: 1.4, m: 0 }}>
              Legacy method — portal link recommended
            </Typography>
          </Box>
        )}

        <SectionLabel>{isLink ? 'Portal options' : 'Spreadsheet options'}</SectionLabel>

        <OptionsPanel visible={isLink}>
          <LinkPermissionOptions
            permissionState={permissionState}
            onPermissionChange={setPermissionState}
          />
        </OptionsPanel>

        <OptionsPanel visible={isSpreadsheet}>
          <Box
            sx={{
              '& .MuiFormControlLabel-root': { alignItems: 'flex-start', mx: 0 },
              '& .MuiFormControlLabel-label': { fontSize: 13 },
              '& .MuiFormLabel-root': { fontSize: 12, color: colors.textSecondary },
            }}
          >
            {open && <SpreadsheetOfferOptions defaultWorkupReportType={defaultWorkupReportType} />}
          </Box>
        </OptionsPanel>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1,
            pt: 0.5,
          }}
        >
          <Button onClick={onClose} sx={CANCEL_BUTTON_SX}>
            Cancel
          </Button>
          <Button onClick={handleContinue} variant="contained" disableElevation sx={PRIMARY_BUTTON_SX}>
            Continue
          </Button>
        </Box>
      </Box>
    </StableMuiDialog>
  );
};

export default TownPortalOffersDialog;
